import {
  clinicMapQueryKey,
  LIVE_MAP_POLL_MS,
} from "@clinicMap/clinicMapQueries";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";

import {
  type DisplayChange,
  displayScreenClient,
  displaysClient,
} from "./displaysClient";

export const displaysKeys = {
  list: (sessionKey: string) => ["displays", sessionKey] as const,
  screen: ["display-screen", "me"] as const,
  view: ["display-screen", "view"] as const,
  // Keyed by display: a browser re-paired to another clinic must not show this one's map.
  liveMap: (displayId: string) =>
    ["display-screen", "live-map", displayId] as const,
  pairing: (sessionKey: string) => ["display-pairings", sessionKey] as const,
};

// A paired screen has no other channel yet that would tell it it was unpaired.
const SCREEN_RECHECK_MS = 30_000;
// The push socket is the fast path; this catches frames lost while it was down.
const VIEW_POLL_MS = 30_000;
// Matches the screen's poll, so a code entered here shows as paired within a beat.
const LIST_REFETCH_MS = 5_000;
const PAIRING_REFETCH_MS = 1_000;
// The screen collects its session on its next poll; give up showing "Pairing" after this.
const PAIRING_WINDOW_MS = 30_000;

/** Approval time by display id, for approvals the screen hasn't collected yet. */
type PendingPairings = Record<string, number>;

export const isPairing = (pending: PendingPairings, displayId: string) =>
  Date.now() - (pending[displayId] ?? 0) < PAIRING_WINDOW_MS;

export const displayScreenQuery = queryOptions({
  queryKey: displaysKeys.screen,
  queryFn: ({ signal }) => displayScreenClient.me(signal),
  staleTime: 0,
  refetchInterval: (query) => (query.state.data ? SCREEN_RECHECK_MS : false),
  // A screen that boots before the network keeps asking until Brian answers.
  retry: true,
});

export const useDisplayView = () =>
  useQuery({
    queryKey: displaysKeys.view,
    queryFn: ({ signal }) => displayScreenClient.view(signal),
    staleTime: 0,
    refetchInterval: VIEW_POLL_MS,
    refetchIntervalInBackground: true,
    retry: true,
  });

/** Only asked while no staff session is signed in. */
export const useDisplayScreen = (enabled: boolean) =>
  useQuery({ ...displayScreenQuery, enabled });

export const usePendingPairings = (sessionKey: string) =>
  useQuery<PendingPairings>({
    queryKey: displaysKeys.pairing(sessionKey),
    queryFn: () => ({}),
    initialData: {},
    staleTime: Infinity,
  }).data;

export const useDisplaysList = (sessionKey: string) => {
  const queryClient = useQueryClient();
  return useSuspenseQuery({
    queryKey: displaysKeys.list(sessionKey),
    queryFn: displaysClient.list,
    refetchInterval: (query) => {
      const pending =
        queryClient.getQueryData<PendingPairings>(
          displaysKeys.pairing(sessionKey),
        ) ?? {};
      const waiting = (query.state.data ?? []).some(
        (display) => !display.device && isPairing(pending, display.id),
      );
      return waiting ? PAIRING_REFETCH_MS : LIST_REFETCH_MS;
    },
  });
};

// Displays rarely change; the confirm prompt only needs to know which rooms have a screen.
const EXAM_ROOMS_STALE_MS = 60_000;

/** Rooms whose exam screen withholds the record until staff confirm the visit. */
export const useExamScreenRoomIds = (sessionKey: string) =>
  useQuery({
    queryKey: displaysKeys.list(sessionKey),
    queryFn: displaysClient.list,
    staleTime: EXAM_ROOMS_STALE_MS,
    select: (displays) =>
      new Set(
        displays
          .filter((d) => d.kind === "exam_room" && d.roomId !== null)
          .map((d) => d.roomId),
      ),
  }).data;

const useInvalidateList = (sessionKey: string) => {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: displaysKeys.list(sessionKey),
      }),
      queryClient.invalidateQueries({
        queryKey: clinicMapQueryKey(sessionKey),
        exact: true,
      }),
    ]);
};

const useSetPairing = (sessionKey: string) => {
  const queryClient = useQueryClient();
  return (displayId: string, approvedAt: number | null) =>
    queryClient.setQueryData<PendingPairings>(
      displaysKeys.pairing(sessionKey),
      (pending = {}) => {
        const { [displayId]: _, ...rest } = pending;
        return approvedAt === null
          ? rest
          : { ...rest, [displayId]: approvedAt };
      },
    );
};

export const useCreateDisplay = (sessionKey: string) => {
  const invalidate = useInvalidateList(sessionKey);
  return useMutation({
    mutationFn: displaysClient.create,
    meta: { handled: true },
    onSuccess: invalidate,
  });
};

export const usePairDisplay = (sessionKey: string) => {
  const invalidate = useInvalidateList(sessionKey);
  const setPairing = useSetPairing(sessionKey);
  return useMutation({
    mutationFn: displaysClient.pair,
    meta: { handled: true },
    onSuccess: (_, { displayId }) => {
      setPairing(displayId, Date.now());
      return invalidate();
    },
  });
};

export const useUnpairDisplay = (sessionKey: string) => {
  const invalidate = useInvalidateList(sessionKey);
  const setPairing = useSetPairing(sessionKey);
  return useMutation({
    mutationFn: displaysClient.unpair,
    meta: { handled: true },
    onSuccess: (_, displayId) => {
      setPairing(displayId, null);
      return invalidate();
    },
  });
};

export const useRemoveDisplay = (sessionKey: string) => {
  const invalidate = useInvalidateList(sessionKey);
  return useMutation({
    mutationFn: displaysClient.remove,
    meta: { handled: true },
    onSuccess: invalidate,
  });
};

/** Fast while the board shows its map; slow otherwise, to learn whether there is one. */
const MAP_HIDDEN_POLL_MS = 30_000;

export const useDisplayLiveMap = (displayId: string, shown: boolean) =>
  useQuery({
    queryKey: displaysKeys.liveMap(displayId),
    queryFn: ({ signal }) => displayScreenClient.liveMap(signal),
    refetchInterval: shown ? LIVE_MAP_POLL_MS : MAP_HIDDEN_POLL_MS,
    refetchIntervalInBackground: true,
  }).data;

export const useUpdateDisplay = (sessionKey: string) => {
  const invalidate = useInvalidateList(sessionKey);
  return useMutation({
    mutationFn: ({
      displayId,
      change,
    }: {
      displayId: string;
      change: DisplayChange;
    }) => displaysClient.update(displayId, change),
    onSuccess: invalidate,
    meta: { handled: true },
  });
};
